import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import type { SeoMetadata } from './marketing-pages';
import {
  MARKETING_IS_PROD,
  getCanonicalUrl,
  getSocialImageByPath,
  toAbsoluteMarketingUrl
} from './marketing-pages';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly productionHosts = new Set(['heyea.ai', 'www.heyea.ai']);

  apply(seo: SeoMetadata): void {
    const canonicalUrl = getCanonicalUrl(seo.canonicalPath);
    const socialImage = seo.socialImagePath
      ? { path: seo.socialImagePath, alt: seo.socialImageAlt ?? seo.title }
      : getSocialImageByPath(seo.canonicalPath);
    const socialImageUrl = toAbsoluteMarketingUrl(socialImage.path);
    const socialImageAlt = seo.socialImageAlt ?? socialImage.alt;
    const robotsDirective = this.resolveRobotsDirective();

    this.title.setTitle(seo.title);

    this.meta.updateTag({ name: 'description', content: seo.description });
    this.meta.updateTag({ name: 'robots', content: robotsDirective });

    this.meta.updateTag({ property: 'og:type', content: seo.ogType ?? 'website' });
    this.meta.updateTag({ property: 'og:title', content: seo.title });
    this.meta.updateTag({ property: 'og:description', content: seo.description });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:site_name', content: 'Heyea' });
    this.meta.updateTag({ property: 'og:image', content: socialImageUrl });
    this.meta.updateTag({ property: 'og:image:secure_url', content: socialImageUrl });
    this.meta.updateTag({ property: 'og:image:alt', content: socialImageAlt });

    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:site', content: seo.twitterSite ?? '@HeyeaAI' });
    this.meta.updateTag({ name: 'twitter:title', content: seo.title });
    this.meta.updateTag({ name: 'twitter:description', content: seo.description });
    this.meta.updateTag({ name: 'twitter:image', content: socialImageUrl });
    this.meta.updateTag({ name: 'twitter:image:alt', content: socialImageAlt });

    this.updateCanonical(canonicalUrl);
    this.updateJsonLd(seo.jsonLd);
  }

  private resolveRobotsDirective(): string {
    if (!MARKETING_IS_PROD) {
      return 'noindex,nofollow';
    }

    if (!this.isBrowser) {
      return 'index,follow';
    }

    const hostname = this.document.location?.hostname?.toLowerCase();
    if (!hostname) {
      return 'index,follow';
    }

    return this.productionHosts.has(hostname) ? 'index,follow' : 'noindex,nofollow';
  }

  private updateCanonical(url: string): void {
    let canonical = this.document.head.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = this.document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      this.document.head.appendChild(canonical);
    }

    canonical.setAttribute('href', url);
  }

  private updateJsonLd(jsonLd: SeoMetadata['jsonLd']): void {
    const scriptId = 'heyea-route-jsonld';
    const existing = this.document.getElementById(scriptId);

    if (existing) {
      existing.remove();
    }

    const script = this.document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    this.document.head.appendChild(script);
  }
}
